import { api } from "./api";
import { toast } from "sonner";

const loadScript = (src) => new Promise((resolve) => {
  if (document.querySelector(`script[src="${src}"]`)) return resolve(true);
  const s = document.createElement("script");
  s.src = src; s.onload = () => resolve(true); s.onerror = () => resolve(false);
  document.body.appendChild(s);
});

/**
 * Open Razorpay checkout for an existing order.
 * onSuccess receives the verified backend response.
 */
export const openRazorpayCheckout = async ({ orderType, orderId, amount, name, email, description, onSuccess }) => {
  // Fetch public razorpay config (key + mode); retry once on transient failure.
  let cfg = null;
  for (let attempt = 0; attempt < 2 && !cfg; attempt++) {
    try {
      cfg = await api.get("/razorpay/config");
    } catch (e) {
      if (attempt === 1) console.warn("[Razorpay] config fetch failed:", e?.message || e);
    }
  }
  const pubKey = cfg?.data?.key_id || null;

  // Free / zero-amount orders never need the gateway — confirm directly
  // (the backend re-checks the stored amount server-side).
  if (!amount || Number(amount) <= 0) {
    try {
      const r = await api.post(`/razorpay/mock-verify`, { order_type: orderType, order_id: orderId });
      toast.success("Order confirmed");
      onSuccess?.(r.data);
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Could not confirm the order");
    }
    return;
  }

  // Paid order but the config call itself failed — don't fall into mock.
  if (!cfg) {
    toast.error("Payment system is temporarily unavailable — please try again in a moment.");
    return;
  }

  // Paid order with no gateway keys configured — mock/test flow.
  if (!pubKey) {
    try {
      const r = await api.post(`/razorpay/mock-verify`, { order_type: orderType, order_id: orderId });
      toast.success("Payment confirmed (mock mode)");
      onSuccess?.(r.data);
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Mock payment failed");
    }
    return;
  }

  const ok = await loadScript("https://checkout.razorpay.com/v1/checkout.js");
  if (!ok) { toast.error("Razorpay SDK could not load"); return; }

  // Create order on backend
  let order;
  try {
    const r = await api.post("/razorpay/create-order", { order_type: orderType, order_id: orderId, amount });
    order = r.data;
  } catch (e) {
    toast.error(e.response?.data?.detail || "Could not create order");
    return;
  }

  const rzp = new window.Razorpay({
    key: pubKey,
    amount: order.amount,
    currency: order.currency || "INR",
    name: "MindDhara",
    description: description || "MindDhara payment",
    order_id: order.razorpay_order_id,
    prefill: { name, email },
    theme: { color: "#2A5C36" },
    handler: async (response) => {
      try {
        const verify = await api.post("/razorpay/verify", {
          order_type: orderType,
          order_id: orderId,
          razorpay_order_id: response.razorpay_order_id,
          razorpay_payment_id: response.razorpay_payment_id,
          razorpay_signature: response.razorpay_signature,
        });
        toast.success("Payment confirmed");
        onSuccess?.(verify.data);
      } catch (e) {
        toast.error("Payment verification failed");
      }
    },
    modal: { ondismiss: () => toast.message("Payment dismissed") },
  });
  rzp.open();
};
